import { isEmptySlateDocument } from "../../core/constants";
import { ATTENDING, MAYBE, NOT_ATTENDING } from "../../guests/constants";
import isEmpty from "lodash/isEmpty";

export const getPageHasGeneralContent = (page = {}) => {
  if (!page) {
    return false;
  }
  return (
    page.title ||
    page.image ||
    page.photo ||
    !isEmptySlateDocument(page.content)
  );
};

export const filterPartyData = (
  sortedPartyData,
  searchTerm,
  selectedEvent,
  hasRepliedFilter,
  hasContactInfoFilter,
  rsvpStatusFilter,
  answersFilter,
) => {
  const filteredSortedPartyData = [];

  for (const partyData of sortedPartyData) {
    // Creating a copy, because modifying the original entry means we can't
    // un-filter after when they're cleared
    const filteredEntry = {
      ...partyData,
    };

    // Do search first before any event specific filtering
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const contactNameMatches =
        filteredEntry.contactName.toLowerCase().indexOf(lowerSearchTerm) > -1;
      const contactInfoMatches =
        filteredEntry.email?.toLowerCase().indexOf(lowerSearchTerm) > -1 ||
        filteredEntry.phoneNumber?.indexOf(lowerSearchTerm) > -1;
      const contactMatches = contactNameMatches || contactInfoMatches;

      if (!contactMatches) {
        // Contact name/info does not match search term, check guests
        const hasMatchingGuest = filteredEntry.guests.some(
          (guest) =>
            guest.firstName?.toLowerCase().indexOf(lowerSearchTerm) > -1 ||
            guest.lastName?.toLowerCase().indexOf(lowerSearchTerm) > -1,
        );

        if (!hasMatchingGuest) {
          // No guests match search term either, do not add party to list
          continue;
        }
      }

      if (selectedEvent && !contactMatches) {
        // Contact doesn't match, only show matching guests when event is selected
        filteredEntry.guests = filteredEntry.guests.filter(
          (guest) =>
            guest.firstName?.toLowerCase().indexOf(lowerSearchTerm) > -1 ||
            guest.lastName?.toLowerCase().indexOf(lowerSearchTerm) > -1,
        );
      }
    }

    // Filter out guests/contacts not invited to the event
    if (selectedEvent) {
      // If no guests are invited, skip this contact
      if (
        !filteredEntry.guests.some(
          (guest) =>
            guest.invites.findIndex(
              (invite) => Number(selectedEvent) === invite.event,
            ) > -1,
        )
      ) {
        continue;
      }

      // Modify the guest list to only show invited guests
      filteredEntry.guests = filteredEntry.guests.filter(
        (guest) =>
          guest.invites.findIndex(
            (invite) => Number(selectedEvent) === invite.event,
          ) > -1,
      );

      // Check RSVP filters
      if (rsvpStatusFilter) {
        if (rsvpStatusFilter === MAYBE) {
          filteredEntry.guests = filteredEntry.guests.filter(
            (guest) => guest.rsvps[selectedEvent]?.rsvpStatus === "maybe",
          );
        } else {
          // WW uses attending; OI uses rsvpStatus only. Match both so OI filter tabs work.
          filteredEntry.guests = filteredEntry.guests.filter((guest) => {
            const rsvp = guest.rsvps[selectedEvent];
            if (rsvpStatusFilter === ATTENDING) {
              return rsvp?.attending === true || rsvp?.rsvpStatus === "yes";
            }
            if (rsvpStatusFilter === NOT_ATTENDING) {
              return rsvp?.attending === false || rsvp?.rsvpStatus === "no";
            }
            return rsvp?.attending === undefined;
          });
        }
      }
      if (!isEmpty(answersFilter)) {
        for (const questionId of Object.keys(answersFilter)) {
          const answerData = answersFilter[questionId];

          // Don't filter if the value to be filtered on doesn't exist
          if (isEmpty(answerData)) {
            continue;
          }

          // Multiple choice, check if answer is in the list
          filteredEntry.guests = filteredEntry.guests.filter((guest) => {
            const rsvp = guest.rsvps[selectedEvent];
            const canHaveAnswers =
              rsvp?.attending === true || rsvp?.rsvpStatus === "maybe";

            if (!rsvp || !canHaveAnswers) {
              return false;
            }

            const answer = rsvp.answers.find(
              (answer) => answer.question === Number(questionId),
            )?.text;

            if (Array.isArray(answerData)) {
              // Multiple choice, check that guest answer is in answer data
              return answerData.includes(answer);
            }

            // Open ended-check that guest answer has a value
            return (
              (answerData === "answered" && Boolean(answer)) ||
              (answerData === "unanswered" && !answer)
            );
          });
        }
      }
    } else {
      // Check summary filters
      if (hasRepliedFilter) {
        // Need to calculate each individually because a party can be updated
        // after RSVP to require an additional reply and fit both criteria
        const partyHasReplied = partyData.guests.some(
          (guest) => !isEmpty(guest.rsvps),
        );
        const partyAwaitingReply = partyData.guests.some((guest) =>
          isEmpty(guest.rsvps),
        );

        // Filter out those not matching reply filter status
        if (
          (hasRepliedFilter === "hasReply" && !partyHasReplied) ||
          (hasRepliedFilter === "awaitingReply" && !partyAwaitingReply)
        ) {
          continue;
        }
      }
      if (hasContactInfoFilter) {
        // Filter out those not matching reply filter status
        if (
          (hasContactInfoFilter === "hasEmail" && !partyData.email) ||
          (hasContactInfoFilter === "hasAddress" && !partyData.address1)
        ) {
          continue;
        }
      }
    }

    filteredSortedPartyData.push(filteredEntry);
  }

  return filteredSortedPartyData;
};
